import { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { Eyebrow, PageHeader } from "../components/bits";
import PlanBuilder from "../components/poc/PlanBuilder";
import { INSTALL_PACKAGES, INSTALL_ADDONS, SERVICE_PLANS, BUSINESS_PLANS, REVO_MAX, REVO_TAG_PLAN, PRICE_TBD } from "../data/content";

const CORD_ITEMS = [
  "Replacement cables",
  "Wall adapters & bricks",
  "Battery wear & replacement",
  "Lost / travel chargers",
];

export default function Plans() {
  const [expand, setExpand] = useState(false);

  return (
    <Layout>
      <PageHeader
        eyebrow="Plans & pricing"
        title="Own the hardware. Subscribe to never plugging in."
        subtitle="One installation, then a small monthly service. You own every Surface and Ceiling Unit outright. The subscription covers what hardware alone can't — enrollment, entitlement, per-device metering, battery-health management, roaming, and lifetime hardware support."
      />

      {/* CORDS COMPARISON */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-navy/10 bg-revo-surface p-8">
              <Eyebrow dark>What cords already cost you</Eyebrow>
              <p className="mt-4 font-display text-4xl font-black text-navy">It quietly adds up.</p>
              <p className="mt-2 text-sm text-navy/60">Replacement chargers, cables, adapters &amp; battery wear.</p>
              <button onClick={() => setExpand((v) => !v)} data-testid="cords-expand-btn" className="mt-4 flex items-center gap-1 text-sm font-semibold text-navy/60 hover:text-navy">
                See what adds up <ChevronDown className={`h-4 w-4 transition-transform ${expand ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {expand && (
                  <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 space-y-2 overflow-hidden">
                    {CORD_ITEMS.map((l) => (
                      <li key={l} className="flex items-center gap-2 border-b border-navy/5 pb-2 text-sm text-navy/70">
                        <Check className="h-4 w-4 shrink-0 text-revo-green" /><span>{l}</span>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col justify-center rounded-3xl border border-revo-green bg-navy p-8">
              <Eyebrow>Revo Home</Eyebrow>
              <p className="mt-4 font-display text-4xl font-black text-revo-green">Pricing revealed at launch</p>
              <p className="mt-4 text-revo-ice">And you own the hardware. Instead of a drawer full of chargers, cables, and worn-out batteries, Revo Home covers every device you own — one simple monthly service.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEP 1: INSTALL PACKAGES */}
      <section className="bg-revo-surface px-5 py-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow dark>Step 1 — one-time</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Choose your installation.</h2>
            <p className="mt-3 text-navy/60">A one-time purchase — you own the hardware.</p>
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {INSTALL_PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.name} delay={(i % 4) * 0.08}>
                <div className={`h-full rounded-3xl border p-6 ${pkg.popular ? "border-revo-green bg-white shadow-[0_0_40px_rgba(47,213,118,0.12)]" : "border-navy/10 bg-white"}`} data-testid={`install-${i}`}>
                  {pkg.popular && <span className="mb-2 inline-block rounded-full bg-revo-green px-3 py-1 text-xs font-bold text-navy">Popular</span>}
                  <h3 className="font-display text-xl font-extrabold text-navy">{pkg.name}</h3>
                  <p className="mt-1 font-display text-base font-bold text-revo-green">{pkg.price}</p>
                  <p className="mt-4 text-sm text-navy/70">{pkg.installed}</p>
                  <p className="mt-3 border-t border-navy/5 pt-3 text-xs text-navy/50">{pkg.charges}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-navy/10 bg-white px-6 py-4" data-testid="install-addons">
              <span className="font-display text-sm font-bold text-navy">Add-ons</span>
              {INSTALL_ADDONS.map((a, i) => (
                <span key={a.name} className="text-sm text-navy/70">
                  {i > 0 && <span className="mr-6 text-navy/20">·</span>}
                  <span className="font-semibold text-navy">{a.name}</span> <span className="text-navy/40">({a.note})</span>
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-revo-green/30 bg-revo-green/[0.06] px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid="revo-tag-addon">
              <div>
                <p className="font-display text-sm font-bold text-navy">{REVO_TAG_PLAN.name} <span className="ml-1 rounded-full bg-revo-green px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-navy">Install once</span></p>
                <p className="mt-1 text-sm text-navy/60">{REVO_TAG_PLAN.included}</p>
              </div>
              <p className="shrink-0 font-display text-sm font-semibold italic text-revo-green">{REVO_TAG_PLAN.standalone}</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-4 flex flex-col gap-5 overflow-hidden rounded-3xl border border-navy bg-navy p-6 text-white md:flex-row md:items-center md:p-8" data-testid="install-revomax">
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:w-64 md:shrink-0">
                <div className="aspect-[4/3] overflow-hidden"><img src={REVO_MAX.imgSrc} alt="Revo Max ten-zone conference charging panel" className="h-full w-full object-cover" /></div>
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="font-display text-2xl font-black tracking-tight">{REVO_MAX.name}</h3>
                  <span className="rounded-full bg-revo-green px-3 py-1 text-xs font-bold text-navy">New — multi-zone</span>
                </div>
                <p className="mt-1 font-display text-lg font-bold text-revo-green">{REVO_MAX.price}</p>
                <p className="mt-3 text-sm leading-relaxed text-revo-ice">{REVO_MAX.installed}. {REVO_MAX.charges}.</p>
                <p className="mt-3 border-t border-white/10 pt-3 text-xs uppercase tracking-[0.15em] text-revo-ice/60">{REVO_MAX.specs}</p>
                <p className="mt-2 text-xs text-revo-ice/70"><span className="font-semibold text-white">Service:</span> {REVO_MAX.service}</p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <p className="mt-6 text-sm text-navy/60" data-testid="financing-line">
              0% financing available: split any installation package over 12 months at checkout — your exact monthly amount appears at checkout once pricing is live.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STEP 2: SERVICE PLANS */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
              <div>
                <Eyebrow dark>Step 2 — monthly</Eyebrow>
                <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Choose your service plan.</h2>
              </div>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {SERVICE_PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div className={`flex h-full flex-col rounded-3xl border p-8 ${p.popular ? "border-revo-green bg-navy text-white" : "border-navy/10 bg-revo-surface text-navy"}`} data-testid={`service-plan-${i}`}>
                  {p.popular && <span className="mb-3 inline-block w-fit rounded-full bg-revo-green px-3 py-1 text-xs font-bold text-navy">Most popular</span>}
                  <h3 className="font-display text-2xl font-extrabold">{p.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="font-display text-2xl font-black">{PRICE_TBD}</span>
                  </div>
                  <p className={`mt-1 text-sm ${p.popular ? "text-revo-ice/70" : "text-navy/50"}`}>{p.serials} device serials</p>
                  <ul className="mt-6 space-y-3 text-sm">
                    {p.includes.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.popular ? "text-revo-green" : "text-revo-green"}`} />
                        <span className={p.popular ? "text-revo-ice" : "text-navy/70"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-6 grid gap-3 rounded-2xl border border-navy/10 bg-revo-surface p-6 sm:grid-cols-3">
              {[["Kitchen service", "Add-on", "Ki appliance entitlement & metering"], ["Extra serials (pack of 5)", "Add-on", "For sensor-heavy homes"], ["Roaming", "Included in every plan", "Charge at any Revo venue"]].map(([n, pr, d]) => (
                <div key={n}>
                  <p className="font-display font-bold text-navy">{n} <span className="text-revo-green">{pr}</span></p>
                  <p className="text-xs text-navy/50">{d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PLAN BUILDER POC */}
      <section className="bg-navy px-5 py-24 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal className="mb-10 text-center">
            <Eyebrow>Build your plan</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-white sm:text-4xl">Three questions. One clear price.</h2>
          </Reveal>
          <Reveal delay={0.1}><PlanBuilder /></Reveal>
        </div>
      </section>

      {/* BUSINESS PLANS */}
      <section className="bg-white px-5 py-20 md:px-8">
        <div className="mx-auto max-w-6xl">
          <Reveal><Eyebrow dark>For business</Eyebrow><h2 className="mt-3 font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Plans that scale with your space.</h2></Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {BUSINESS_PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div className="h-full rounded-3xl border border-navy/10 bg-revo-surface p-8" data-testid={`business-plan-${i}`}>
                  <h3 className="font-display text-xl font-extrabold text-navy">{p.name}</h3>
                  {p.subtitle && <p className="mt-1 text-sm font-semibold uppercase tracking-[0.15em] text-navy/40">{p.subtitle}</p>}
                  <p className="mt-3 font-semibold text-revo-green">{p.pricing}</p>
                  <p className="mt-4 text-sm leading-relaxed text-navy/60">{p.includes}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 rounded-2xl border border-navy/10 bg-revo-surface px-6 py-4 text-sm leading-relaxed text-navy/70" data-testid="pro-venue-callout">
              <span className="font-semibold text-navy">Pro vs Venue:</span> Pro covers your own employees&apos; devices. Venue adds guest day-passes, roaming settlement, and visitor analytics for customers who aren&apos;t on your account.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDING MEMBERS */}
      <section className="bg-revo-green px-5 py-20 md:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-black tracking-tight text-navy sm:text-5xl">First 1,000 homes: installation labor free.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-navy/80">Reserve your spot and we&apos;ll waive professional installation on any package — plus first hardware allocation when we ship.</p>
            <p className="mt-6 text-xs text-navy/60">All prices in USD. Annual billing ≈ two months free. You own your hardware; cancel the service anytime and it keeps working as standard chargers for your own devices. Taxes and Kitchen installation quoted at checkout.</p>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
